import { CourseTreeNodeTypeEnum, type CourseTreeNode } from "@/types/generated";

export interface NodeIndexContext {
  module: CourseTreeNode | null;
  chapter: CourseTreeNode | null;
  subchapter: CourseTreeNode | null;
  parent: CourseTreeNode | null;
  moduleIndex: number | null;
  chapterIndex: number | null;
}

export const EMPTY_NODE_INDEX_CONTEXT: NodeIndexContext = Object.freeze({
  module: null,
  chapter: null,
  subchapter: null,
  parent: null,
  moduleIndex: null,
  chapterIndex: null,
});

export interface SiblingCounters {
  module: number;
  chapter: number;
}

export interface NextNodeContextResult {
  context: NodeIndexContext;
  counters: SiblingCounters;
}

/**
 * Compute the ancestor context that applies *inside* the given node, plus the
 * sibling counters for the next call at the same level. Pure: returns new
 * objects, never mutates inputs.
 *
 * - Module: starts a fresh chapter section (chapter/chapterIndex reset to null)
 *   and bumps the module counter.
 * - Chapter: bumps the chapter counter, keeps the parent's module/subchapter.
 * - Subchapter: stores the node as the current subchapter; counters unchanged.
 * - ContentElement (or anything else): pass-through — context and counters
 *   unchanged.
 */
export const nextNodeContext = (
  node: CourseTreeNode,
  context: NodeIndexContext,
  counters: SiblingCounters
): NextNodeContextResult => {
  if (node.type === CourseTreeNodeTypeEnum.Module) {
    const newCounters: SiblingCounters = {
      module: counters.module + 1,
      chapter: counters.chapter,
    };
    return {
      context: {
        module: node,
        chapter: null,
        subchapter: context.subchapter,
        parent: context.parent,
        moduleIndex: newCounters.module,
        chapterIndex: null,
      },
      counters: newCounters,
    };
  }
  if (node.type === CourseTreeNodeTypeEnum.Chapter) {
    const newCounters: SiblingCounters = {
      module: counters.module,
      chapter: counters.chapter + 1,
    };
    return {
      context: {
        ...context,
        chapter: node,
        chapterIndex: newCounters.chapter,
      },
      counters: newCounters,
    };
  }
  if (node.type === CourseTreeNodeTypeEnum.Subchapter) {
    return {
      context: { ...context, subchapter: node },
      counters,
    };
  }
  return { context, counters };
};
